// GraphQL field declaration for the fulfillment drawer's orderDetails-v2 request (OOP-4197) —
// deliberately a SEPARATE dataModel from orderDetailsDataModel.ts, not an extension of it. That
// existing model backs AllOrdersTable's already-working ExpandedOrderDetailsPanel/
// OrderDetailsFetcher; a wrong/rejected field name here should only break the new drawer path.
//
// The `headers.omsVersion` / `request` shape below (userId, headers{tmxProfileId, channelId,
// omsVersion}, request{customerAccountId, storeNumber, transactionId, registerNumber, orderNumber,
// salesDate, searchType, groupAndSortBy{groupBy}}, response{statusDescription, lineItems[
// {itemDescFromCatalog, quantity, imageUrl, skuNumber}]}) is the confirmed baseline. `omsVersion`
// is how this ticket's "sets the omsVersion cookie to v2" AC is actually satisfied — it is a
// GraphQL request field, not a real browser cookie, following the exact same `HeaderExtras`
// pattern activeOrdersDataModel.ts already uses for tmxProfileId/channelId.
//
// `fulfillmentGroups` and everything nested under it (storePickup/shippingAndHandling/
// bodfsDeliveryAndHandling/lineItems[].statusCode) are layered on top of that confirmed baseline
// and are NOT confirmed against a live schema — they are a best-effort guess at what
// src/components/common/FulfillmentDrawer/** actually reads off its `fulfillmentGroup` prop
// (ported one-to-one from @thd-olt-component-react/active-orders, OOP-4059). Deliberately absent:
// `deliveryTrackerData` — that comes from a separate REST call (useDeliveryTrackerQuery.ts),
// exactly as it already does for AllOrdersTable; see ActiveOrdersTable.tsx. If any of these
// speculative names are rejected as FieldUndefined, only this query fails — see
// src/data/types.ts's RawOrderDetailsV2Response for the matching TypeScript shape and
// src/data/adapters/fulfillmentDrawer.ts for how a failed/partial result degrades gracefully.
import { params, shape, arrayOf, string, id, number, bool, customType } from '@thd-nucleus/data-sources';

export type OrderDetailsV2QueryParams =
  | {
      variant: 'online';
      userId: string;
      customerAccountId: string;
      orderNumber: string;
      tmxProfileId?: string;
      channelId?: string;
    }
  | {
      variant: 'inStore';
      userId: string;
      storeNumber: number;
      transactionId: number;
      registerNumber: number;
      salesDate: string;
      tmxProfileId?: string;
      channelId?: string;
    };

export function buildOrderDetailsV2Variables(queryParams: OrderDetailsV2QueryParams) {
  const { userId, tmxProfileId, channelId } = queryParams;
  const headers = { tmxProfileId, channelId, omsVersion: 'v2' };

  if (queryParams.variant === 'online') {
    return {
      userId,
      headers,
      request: {
        customerAccountId: queryParams.customerAccountId,
        orderNumber: queryParams.orderNumber,
        searchType: 'online'
      }
    };
  }

  return {
    userId,
    headers,
    request: {
      storeNumber: queryParams.storeNumber,
      transactionId: queryParams.transactionId,
      registerNumber: queryParams.registerNumber,
      salesDate: queryParams.salesDate,
      searchType: 'inStore'
    }
  };
}

export const orderDetailsV2DataModel = {
  orderDetails: params({
    userId: id().isRequired(),
    headers: customType('HeaderExtras').shape({
      tmxProfileId: string(),
      channelId: string(),
      omsVersion: string()
    }),
    request: customType('OrderDetailsRequest!').shape({
      customerAccountId: string(),
      storeNumber: number(),
      transactionId: number(),
      registerNumber: number(),
      orderNumber: string(),
      salesDate: string(),
      searchType: string(),
      groupAndSortBy: shape({ groupBy: string() })
    })
  }).shape({
    statusDescription: string(),
    lineItems: arrayOf(
      shape({
        itemDescFromCatalog: string(),
        quantity: number(),
        imageUrl: string(),
        skuNumber: number()
      })
    ),
    // See this file's own header comment — everything below is speculative/unconfirmed.
    fulfillmentGroups: arrayOf(
      shape({
        fulfillmentType: string(),
        fulfillmentDate: string(),
        storePickup: shape({
          isCurbsidePickupAvailable: bool(),
          IsInLocker: bool(),
          store: shape({
            name: string(),
            number: string(),
            phone: string(),
            address: shape({ city: string(), state: string(), zipCode: string() })
          }),
          pickUpPerson: shape({ name: shape({ firstName: string(), lastName: string() }) })
        }),
        // No `deliveryTrackerData` here — carrier-tracking status/links come from the SEPARATE
        // deliveryTracker REST endpoint (useDeliveryTrackerQuery.ts), same as AllOrdersTable.
        // ActiveOrdersTable.tsx splices that result into the merged fulfillmentGroup object
        // itself, not this query. See types.ts's RawOrderDetailsV2Response header comment.
        shippingAndHandling: shape({
          deliveryAddress: shape({
            name: shape({ firstName: string(), lastName: string() }),
            postalDetails: shape({
              addressLine1: string(),
              addressLine2: string(),
              city: string(),
              state: string(),
              zipCode: string()
            })
          }),
          shipments: shape({ shipment: arrayOf(shape({ estimatedDeliveryDate: string() })) }),
          estEndDeliveryDate: string(),
          applianceDeliveries: arrayOf(shape({ currentDeliveryDate: string(), originalDeliveryDate: string() }))
        }),
        bodfsDeliveryAndHandling: shape({
          bodfsDeliveries: arrayOf(shape({ reservationWindowStartTime: string(), reservationWindowEndTime: string() }))
        }),
        lineItems: arrayOf(
          shape({
            statusCode: number(),
            statusDescription: string(),
            quantity: number(),
            unitPrice: number({ float: true }),
            brandName: string(),
            description: string(),
            subClassName: string(),
            imageUrl: string(),
            pickupDate: string(),
            deliveredDateArray: arrayOf(string())
          })
        )
      })
    )
  })
};
